/**
 * Global behavior signal helper for API integration
 *
 * This module provides a way to automatically include behavior signals
 * in all API requests without changing individual components.
 */

import { getCollectorInstance, getBehaviorSignal } from './behaviorSignal.mjs';
import { getNonce } from './nonce.mjs';

/**
 * Get the current behavior signal as a base64-encoded string for use in HTTP headers
 * Returns null if collector is not initialized or disabled
 * Automatically fetches and includes a nonce for attestation
 *
 * @param {string} action - Optional action identifier for nonce (e.g., 'login', 'signup')
 * @returns {Promise<string|null>} Base64-encoded behavior signal or null
 */
export async function getBehaviorSignalHeader(action = null) {
  try {
    const collector = getCollectorInstance();

    if (!collector || !collector.options.enabled) {
      return null;
    }

    // Fetch nonce for attestation
    let nonce = null;
    try {
      nonce = await getNonce(action);
    } catch (nonceError) {
      console.warn('[BehaviorSignal] Failed to fetch nonce, continuing without it:', nonceError);
      // Continue without nonce - better to send signal without nonce than to fail completely
    }

    const signal = getBehaviorSignal(nonce);

    if (!signal) {
      return null;
    }

    // Convert to base64
    const jsonStr = JSON.stringify(signal);
    const base64 = btoa(jsonStr);

    // Check size (most servers limit headers to ~8KB)
    const sizeBytes = new Blob([base64]).size;
    if (sizeBytes > 7000) {
      // Too large for header, skip
      console.warn('[BehaviorSignal] Signal too large for header:', sizeBytes, 'bytes');
      return null;
    }

    return base64;
  } catch (error) {
    console.error('[BehaviorSignal] Error getting signal header:', error);
    return null;
  }
}

/**
 * Get just the risk score and flags for a lightweight header
 * Automatically fetches and includes a nonce for attestation
 *
 * @param {string} action - Optional action identifier for nonce
 * @returns {Promise<string|null>} Base64-encoded minimal signal or null
 */
export async function getBehaviorSignalHeaderMinimal(action = null) {
  try {
    const collector = getCollectorInstance();

    if (!collector || !collector.options.enabled) {
      return null;
    }

    // Fetch nonce for attestation
    let nonce = null;
    try {
      nonce = await getNonce(action);
    } catch (nonceError) {
      console.warn('[BehaviorSignal] Failed to fetch nonce, continuing without it:', nonceError);
    }

    const signal = getBehaviorSignal(nonce);

    if (!signal) {
      return null;
    }

    // Only send essential data
    const minimalSignal = {
      v: signal.version,
      ts: signal.ts,
      nonce: signal.nonce,
      action: signal.action,
      risk: signal.derived.riskScore,
      flags: signal.derived.flags
    };

    const jsonStr = JSON.stringify(minimalSignal);
    return btoa(jsonStr);
  } catch (error) {
    console.error('[BehaviorSignal] Error getting minimal signal header:', error);
    return null;
  }
}
